import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllArticles, deleteArticleApi, updateArticleStatusApi, downloadExcelTemplate,
  bulkUploadArticles
} from "../../../../services/Article/Article.service";
import { getCategories } from "../../../../services/Category/category.service";
import { getAllDesired } from "../../../../services/DesiredType/DesiredType.service";
import { Pencil, Eye, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { Upload, Download } from "lucide-react";
import "./ArticleList.css";

const ArticleList: React.FC = () => {
  const [articles, setArticles] = useState<any[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [metalTypes, setMetalTypes] = useState<any[]>([]);
  const [selectedMetal, setSelectedMetal] = useState("");
  const [selectedArticleName, setSelectedArticleName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    fetchArticles();
    fetchMetalTypes();
  }, []);

  const fetchMetalTypes = async () => {
    try {
      const res = await getAllDesired();
      setMetalTypes(res.data || []);
    } catch (err) {
      // console.log("Metal fetch error", err);
    }
  };

  const fetchArticles = async () => {
    try {
      const data = await getAllArticles();
      setArticles(data);
    } catch (error) {
      console.error("Error fetching articles:", error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadExcelTemplate();
      toast.success("Template downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download template!");
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value.toLowerCase());
    setCurrentPage(1);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = [".xlsx", ".xls"];
      const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        toast.error("Please upload a valid Excel file (.xlsx or .xls)");
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first!");
      return;
    }

    setUploading(true);
    try {
      await bulkUploadArticles(selectedFile);
      toast.success("Articles uploaded successfully!");
      setShowUploadModal(false);
      setSelectedFile(null);
      fetchArticles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to upload articles!");
    } finally {
      setUploading(false);
    }
  };

  const toggleStatus = async (id: number) => {
    try {
      await updateArticleStatusApi(id);

      const updated = articles.map((item) =>
        item.id === id
          ? {
            ...item,
            isActive: !item.isActive,
            status: item.isActive ? "Inactive" : "Active",
          }
          : item
      );

      setArticles(updated);
    } catch (error) {
      toast.error("Failed to update status!");
      return;
    }
    toast.success(`Status updated successfully`);
  };

  const articleNames = [
    ...new Set(
      articles
        .map((item) => item.articleName?.trim())
        .filter(Boolean)
    ),
  ];

  const categoryList = [
    ...new Map(
      articles
        .filter((item) => item.category)
        .map((item) => [
          item.category.id,
          {
            ...item.category,
            name: item.category.name?.trim(),
          },
        ])
    ).values(),
  ];

  const confirmDelete = async () => {
    if (deleteId === null) return;

    try {
      await deleteArticleApi(deleteId);
      fetchArticles();
      setDeleteId(null);
      toast.success("Article deleted successfully!");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete article!");
    }
  };

  // Filter articles based on all filters
  const filteredArticles = articles.filter((item) => {
    const articleName = item.articleName?.trim().toLowerCase() || "";
    const categoryName = item.category?.name?.trim().toLowerCase() || "";
    const metalName = item.metalType?.name?.toLowerCase() || "";

    // Search term filter (searches in article name)
    const matchSearch = searchTerm === "" || articleName.includes(searchTerm);

    // Article name exact match filter
    const matchArticle = selectedArticleName === "" || 
      item.articleName?.trim() === selectedArticleName;

    // Category exact match filter
    const matchCategory = selectedCategory === "" || 
      item.category?.name?.trim() === selectedCategory;

    // Metal type exact match filter
    const matchMetal = selectedMetal === "" || 
      item.metalType?.name === selectedMetal;

    return matchSearch && matchArticle && matchCategory && matchMetal;
  });

  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedData = filteredArticles
    .slice(startIndex, startIndex + itemsPerPage)
    .map((item, index) => ({
      ...item,
      serial: startIndex + index + 1,
    }));

  // Reset all filters
  const resetAllFilters = () => {
    setSearchTerm("");
    setSelectedArticleName("");
    setSelectedCategory("");
    setSelectedMetal("");
    setCurrentPage(1);
  };

  return (
    <div className="article-list-container">
      <div className="header-bar">
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>

          {/* 🔍 SEARCH - searches in article names */}
          <input
            type="text"
            placeholder="Search by Article Name..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />

          {/* 📦 ARTICLE DROPDOWN */}
          <select
            className="filter-select"
            value={selectedArticleName}
            onChange={(e) => {
              setSelectedArticleName(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Articles</option>
            {articleNames.map((name, i) => (
              <option key={i} value={name}>
                {name}
              </option>
            ))}
          </select>

          {/* 📂 CATEGORY DROPDOWN */}
          <select
            className="filter-select"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Categories</option>
            {categoryList.map((cat: any) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* 🪙 METAL DROPDOWN */}
          <select
            className="filter-select"
            value={selectedMetal}
            onChange={(e) => {
              setSelectedMetal(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Metal Types</option>
            {metalTypes.map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </select>

          {/* Reset Filters Button */}
          {(searchTerm || selectedArticleName || selectedCategory || selectedMetal) && (
            <button className="reset-filters-btn" onClick={resetAllFilters}>
              Clear All Filters
            </button>
          )}
        </div>

        <div className="button-group">
          <button
            className="download-template-btn"
            onClick={handleDownloadTemplate}
          >
            <Download size={16} />
            Download Template
          </button>

          <button
            className="upload-btn"
            onClick={() => setShowUploadModal(true)}
          >
            <Upload size={16} />
            Bulk Upload
          </button>

          <button
            className="add-btn"
            onClick={() => navigate("/add/article")}
          >
            Add Article
          </button>
        </div>
      </div>

      {/* Results info */}
      <div style={{ marginBottom: "10px", fontSize: "14px", color: "#666" }}>
        Showing {paginatedData.length} of {filteredArticles.length} articles
      </div>

      <table className="article-table">
        <thead>
          <tr>
            <th>S.NO</th>
            <th>Name</th>
            <th>Metal Type</th>
            <th>Category</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {paginatedData.length > 0 ? (
            paginatedData.map((item) => (
              <tr key={item.id}>
                <td>{item.serial}</td>
                <td>{item.articleName}</td>
                <td>{item.metalType?.name}</td>
                <td>{item.category?.name}</td>
                <td>
                  <button
                    className={`status-btn ${item.isActive ? "active" : "inactive"}`}
                    onClick={() => toggleStatus(item.id)}
                  >
                    {item.isActive ? "Active" : "Inactive"}
                  </button>
                </td>
                <td>
                  <div className="action-icons">
                    <button
                      className="icon-btn"
                      title="View"
                      onClick={() => setSelectedArticle(item)}
                    >
                      <Eye size={16} />
                    </button>

                    <button
                      className="icon-btn"
                      title="Edit"
                      onClick={() => navigate(`/edit/article/${item.id}`)}
                    >
                      <Pencil size={16} />
                    </button>

                    <button
                      className="icon-btn delete"
                      title="Delete"
                      onClick={() => setDeleteId(item.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", padding: "40px" }}>
                No articles found matching the selected filters
              </td>
            </tr>
          )}
        </tbody>
       </table>

      {/* Pagination Controls */}
      {totalPages > 0 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* View Modal */}
      {selectedArticle && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedArticle(null)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Article Details</h3>
            <table className="details-table">
              <tbody>
                <tr>
                  <td><strong>ID</strong></td>
                  <td>{selectedArticle.id}</td>
                </tr>
                <tr>
                  <td><strong>Name</strong></td>
                  <td>{selectedArticle.articleName}</td>
                </tr>
                <tr>
                  <td><strong>Metal Type</strong></td>
                  <td>{selectedArticle.metalType?.name}</td>
                </tr>
                <tr>
                  <td><strong>Category</strong></td>
                  <td>{selectedArticle.category?.name}</td>
                </tr>
                <tr>
                  <td><strong>Status</strong></td>
                  <td>{selectedArticle.status}</td>
                </tr>
              </tbody>
            </table>

            <button
              className="add-btn"
              style={{ marginTop: "15px" }}
              onClick={() => setSelectedArticle(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId !== null && (
        <div
          className="modal-overlay"
          onClick={() => setDeleteId(null)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Confirm Delete</h3>
            <p style={{ margin: "15px 0" }}>
              Are you sure you want to delete this article?
            </p>

            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <button
                className="add-btn delete"
                onClick={confirmDelete}
              >
                Yes
              </button>

              <button
                className="add-btn"
                onClick={() => setDeleteId(null)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Excel Modal */}
      {showUploadModal && (
        <div
          className="modal-overlay center_modal_class"
          onClick={() => {
            setShowUploadModal(false);
            setSelectedFile(null);
          }}
        >
          <div
            className="modal-content upload-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Bulk Upload Articles</h3>

            <div className="upload-instructions">
              <p>Please follow these steps:</p>
              <ol>
                <li>Download the Excel template using the "Download Template" button</li>
                <li>Fill in the data in the template</li>
                <li>Upload the filled Excel file here</li>
              </ol>
              <p className="note">
                <strong>Note:</strong> Required columns: Metal Type ID, Category ID, Article Name, Description (optional)
              </p>
            </div>

            <div className="file-upload-area">
              <input
                type="file"
                id="excel-file-input"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <button
                className="select-file-btn"
                onClick={() => document.getElementById("excel-file-input")?.click()}
              >
                {selectedFile ? "Change File" : "Select Excel File"}
              </button>

              {selectedFile && (
                <div className="selected-file-info">
                  <span>Selected: {selectedFile.name}</span>
                  <button
                    className="clear-file-btn"
                    onClick={() => setSelectedFile(null)}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div className="modal-buttons">
              <button
                className="upload-submit-btn"
                onClick={handleBulkUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>

              <button
                className="cancel-btn"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleList;